/** Карточка одного уведомления в дропдауне.
 *
 *  Рендер выбирается по `source_code`:
 *  * `terminal_pending` — hard-drive в --warning-soft, actions: Approve/Reject.
 *  * `driver_message_incoming` — message-square в --info-soft, actions: Resolve/Reject/Open.
 *
 *  Inline-action идёт в `actions[]` от бэка (см. `apps.notifications.sources.devices`).
 *  Здесь маппим `action.code` на иконку/цвет/обработчик.
 */
import { type Component, For, Show, createMemo } from "solid-js";
import {
  Check,
  CheckCheck,
  ClipboardList,
  ExternalLink,
  HardDrive,
  MessageSquare,
  X,
  type LucideProps,
} from "lucide-solid";
import toast from '~/lib/toast';
import { apiRequest } from "~/lib/apiRequest";
import { useI18n } from "~/i18n";
import { useNotifications } from "~/contexts/NotificationsProvider";
import type { Notification, NotificationAction } from "~/lib/notifications";

interface Props {
  notification: Notification;
  onClose: () => void;
  /** «Открыть» — показать модалку с полной информацией об уведомлении. */
  onOpenDetail: (n: Notification) => void;
}

interface IconStyle {
  Icon: Component<LucideProps>;
  iconColorClass: string;
  bgClass: string;
}

function iconStyleFor(code: string): IconStyle {
  switch (code) {
    case "terminal_pending":
      return { Icon: HardDrive, iconColorClass: "text-warning", bgClass: "bg-warning-soft" };
    case "driver_message_incoming":
      return { Icon: MessageSquare, iconColorClass: "text-info", bgClass: "bg-info-soft" };
    case "driver_registration":
      return { Icon: ClipboardList, iconColorClass: "text-primary", bgClass: "bg-primary-soft" };
    default:
      return { Icon: HardDrive, iconColorClass: "text-ink-muted", bgClass: "bg-surface-3" };
  }
}

interface ActionStyle {
  Icon: Component<LucideProps>;
  classes: string;
  label: string;
  errorKey: string;
}

function actionStyle(t: (k: string) => string, code: string): ActionStyle {
  switch (code) {
    case "approve":
      return {
        Icon: Check,
        classes: "border-primary bg-primary-soft text-primary hover:bg-primary hover:text-white",
        label: t("notifications.actions.approve"),
        errorKey: "notifications.errors.approveFail",
      };
    case "reject":
      return {
        Icon: X,
        classes: "border-border bg-surface-3 text-danger hover:bg-danger-soft",
        label: t("notifications.actions.reject"),
        errorKey: "notifications.errors.rejectFail",
      };
    case "resolve":
      return {
        Icon: CheckCheck,
        classes: "border-primary bg-primary-soft text-primary hover:bg-primary hover:text-white",
        label: t("notifications.actions.resolve"),
        errorKey: "notifications.errors.resolveFail",
      };
    case "open":
      return {
        Icon: ExternalLink,
        classes: "border-border bg-surface-3 text-ink hover:bg-surface-hover",
        label: t("notifications.actions.open"),
        errorKey: "notifications.errors.approveFail",
      };
    default:
      return {
        Icon: ExternalLink,
        classes: "border-border bg-surface-3 text-ink hover:bg-surface-hover",
        label: code,
        errorKey: "notifications.errors.approveFail",
      };
  }
}

function relativeTime(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then) / 1000;
  if (diff < 45) return locale === "ru" ? "сейчас" : "now";
  if (diff < 90) return locale === "ru" ? "1 мин" : "1m";
  if (diff < 3600) return `${Math.round(diff / 60)} ${locale === "ru" ? "мин" : "m"}`;
  if (diff < 7200) return locale === "ru" ? "1 ч" : "1h";
  if (diff < 86400) return `${Math.round(diff / 3600)} ${locale === "ru" ? "ч" : "h"}`;
  if (diff < 172800) return locale === "ru" ? "вчера" : "yesterday";
  return `${Math.round(diff / 86400)} ${locale === "ru" ? "дн" : "d"}`;
}

export const NotificationItem: Component<Props> = (props) => {
  const { t, locale } = useI18n();
  const { markRead, refetch } = useNotifications();

  const style = iconStyleFor(props.notification.source_code);
  const unread = createMemo(() => !props.notification.is_read);
  const resolved = createMemo(() => props.notification.resolved_at !== null);

  const payloadAsParams = createMemo<Record<string, string | number>>(() => {
    const p = props.notification.payload ?? {};
    const out: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string" || typeof v === "number") out[k] = v;
    }
    return out;
  });

  const titleKey = `notifications.types.${props.notification.source_code}.title`;
  const subtitleKey = `notifications.types.${props.notification.source_code}.subtitle`;

  const openDetail = () => {
    // Показать модалку с полной информацией. Помечаем прочитанным и открываем
    // детальный просмотр.
    void markRead(props.notification.id);
    props.onOpenDetail(props.notification);
  };

  const handleAction = async (act: NotificationAction) => {
    // «Открыть» и любое другое GET-действие = показать детальную модалку
    // (а не deeplink на пустую страницу). Ловим по методу, а не только по коду
    // 'open', чтобы кнопка «Открыть» у ЛЮБОГО типа уведомления (терминал,
    // сообщение, регистрация) гарантированно открывала окно.
    if (act.method === "GET" || act.code === "open") {
      openDetail();
      return;
    }
    try {
      await apiRequest(act.method, act.url.replace(/^\//, ""));
      void markRead(props.notification.id);
      // resolve() на бэке снимет уведомление через WS-push, но fallback'ом
      // дёрнем refetch на всякий случай.
      void refetch();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      toast.error(`${t(actionStyle(t, act.code).errorKey)} · ${message}`);
    }
  };

  return (
    <div
      role="button"
      tabindex="0"
      title={t("notifications.actions.open")}
      onClick={() => openDetail()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDetail();
        }
      }}
      class="flex cursor-pointer items-start gap-3 border-b border-border-faint px-4 py-3 transition-colors hover:brightness-[0.98]"
      classList={{
        "bg-surface-2 opacity-70": resolved(),
        "bg-info-soft": !resolved() && unread(),
        "bg-surface": !resolved() && !unread(),
      }}
    >
      <div
        class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
        classList={{
          [style.bgClass]: !resolved(),
          "bg-surface-3": resolved(),
        }}
      >
        <style.Icon
          size={18}
          stroke-width={1.8}
          class={resolved() ? "text-ink-muted" : style.iconColorClass}
        />
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-2">
          <div
            class="flex-1 truncate text-sm font-semibold"
            classList={{ "text-ink": !resolved(), "text-ink-muted line-through": resolved() }}
          >
            {t(titleKey, payloadAsParams())}
          </div>
          <span class="flex-shrink-0 text-xs font-medium text-ink-muted">
            {relativeTime(props.notification.created_at, locale())}
          </span>
        </div>
        <div class="mt-0.5 text-xs text-ink-muted">
          {t(subtitleKey, payloadAsParams())}
        </div>
        <Show when={typeof props.notification.payload?.preview === "string" && props.notification.payload.preview}>
          <div class="mt-1 line-clamp-2 text-xs text-ink">
            «{String(props.notification.payload.preview)}»
          </div>
        </Show>
        <Show when={resolved()}>
          <div class="mt-1.5 inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-2xs font-semibold text-success-soft-ink">
            {t("notifications.resolvedBadge")}
          </div>
        </Show>
        <Show when={!resolved() && props.notification.actions.length > 0}>
          <div class="mt-2 flex flex-wrap gap-1.5">
            <For each={props.notification.actions}>
              {(act) => {
                const styled = actionStyle(t, act.code);
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      // Не даём клику «всплыть» до карточки (она тоже открывает
                      // детальный просмотр) — иначе approve/reject срабатывали бы
                      // вместе с открытием окна.
                      e.stopPropagation();
                      void handleAction(act);
                    }}
                    class={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${styled.classes}`}
                  >
                    <styled.Icon size={12} stroke-width={2} />
                    <span>{styled.label}</span>
                  </button>
                );
              }}
            </For>
          </div>
        </Show>
      </div>
    </div>
  );
};
